import React, { useEffect, useState, useRef, useCallback } from "react"; // Added useCallback
import { useReactFlow } from "reactflow";
import { useClipboardImage } from "../../hooks/useClipboardImage";
import { GroupNodeData } from "./types";
import { HandleConfig } from "../../types"; // Import HandleConfig
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager"; // Import HandleManager
import IconSelector from "../../components/shared/IconSelector";
import ColorPicker from "../../components/shared/ColorPicker";

interface GroupConfigBoxProps {
  nodeId: string;
  data: GroupNodeData; // This type now includes handles
  onUpdate?: (data: Partial<GroupNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  isDisabled?: boolean;
  portalRoot?: HTMLElement | null;
}


// Custom label suggestions (reused from ClusterConfigBox)
const CUSTOM_LABEL_SUGGESTIONS = [
  { value: "Production", label: "Production" },
  { value: "Development", label: "Development" },
  { value: "Staging", label: "Staging" },
  { value: "Beta", label: "Beta" },
  { value: "Internal", label: "Internal" },
  { value: "High Priority", label: "High Priority" },
  { value: "Low Priority", label: "Low Priority" },
  { value: "Critical", label: "Critical" },
  { value: "Experimental", label: "Experimental" },
  { value: "Legacy", label: "Legacy" },
  { value: "New", label: "New" },
  { value: "Deprecated", label: "Deprecated" },
  { value: "Custom", label: "Custom" },
];

export default function GroupConfigBox({
  data,
  nodeId,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  isAdvancedMode = false,
  isDisabled = false,
  portalRoot,
}: GroupConfigBoxProps) {
  // Get the ReactFlow instance for image capture
  const reactFlowInstance = useReactFlow();
  // Initialize the clipboard image hook
  const { copySelectedAsImage } = useClipboardImage(reactFlowInstance);
  // Initialize state with props
  const [name, setName] = useState(data.name || "");
  const [color, setColor] = useState<string | undefined>(data.color); // Use optional string for hex color
  const [colorOpacity, setColorOpacity] = useState(data.colorOpacity ?? 0.3); // Default to 30% opacity
  const [icon, setIcon] = useState(data.icon || "layers");
  const [customLabel, setCustomLabel] = useState(data.customLabel || "");
  // const [hasIncomingHandle, setHasIncomingHandle] = useState(data.hasIncomingHandle === true ? true : false); // Removed
  // const [hasOutgoingHandle, setHasOutgoingHandle] = useState(data.hasOutgoingHandle === true ? true : false); // Removed
  // --- Add State for Handles ---
  const [handles, setHandles] = useState<HandleConfig[]>(() => {
    return Array.isArray(data.handles) ? [...data.handles] : [];
  });
  // --- End Add State for Handles ---

  // Initialize visibility settings
  const [groupNameVisibility, setGroupNameVisibility] = useState<VisibilityOption>(
    data.groupNameVisibility === "hidden" ? "hidden" : "visible"
  );
  const [iconVisibility, setIconVisibility] = useState<VisibilityOption>(
    data.iconVisibility === "hidden" ? "hidden" : "visible"
  );
  const [customLabelVisibility, setCustomLabelVisibility] = useState<VisibilityOption>(
    data.customLabelVisibility === "hidden" ? "hidden" : "visible"
  );

  // Track previous node ID to detect changes
  const prevNodeIdRef = useRef<string | null>(null);

  // Track previous update to prevent loops and for change detection
  const prevUpdateRef = useRef("");

  // Flag to prevent updates during state synchronization
  const isSyncingRef = useRef(false);

  // Initialize or update state from props when the node changes (selected node changes)
  useEffect(() => {
    const currentNodeId = (data as any).id || JSON.stringify(data);

    if (prevNodeIdRef.current !== currentNodeId) {
      isSyncingRef.current = true; // Start sync

      setName(data.name || "");
      setColor(data.color); // Sync optional hex color
      setColorOpacity(data.colorOpacity ?? 0.3); // Sync opacity with default 30%
      setIcon(data.icon || "layers");
      setCustomLabel(data.customLabel || "");
      // setHasIncomingHandle(data.hasIncomingHandle === true ? true : false); // Removed
      // setHasOutgoingHandle(data.hasOutgoingHandle === true ? true : false); // Removed

      // Initialize visibility settings
      setGroupNameVisibility(data.groupNameVisibility === "hidden" ? "hidden" : "visible");
      setIconVisibility(data.iconVisibility === "hidden" ? "hidden" : "visible");
      setCustomLabelVisibility(data.customLabelVisibility === "hidden" ? "hidden" : "visible");

      // --- Initialize Handles State on Node Change ---
      setHandles(() => {
        return Array.isArray(data.handles) ? [...data.handles] : [];
      });
      // --- End Initialize Handles State ---

      prevNodeIdRef.current = currentNodeId;

      // Use setTimeout to reset the flag after state updates likely settle
      // and reset the prevUpdateRef to the newly synced state
      setTimeout(() => {
        isSyncingRef.current = false; // End sync
        // Reset prevUpdateRef *after* syncing to allow the first user change to trigger an update
        prevUpdateRef.current = JSON.stringify({
          name: data.name || "",
          color: data.color, // Use synced color
          colorOpacity: data.colorOpacity ?? 0.3, // Include opacity in initial sync ref with default 30%
          icon: data.icon || "layers",
          customLabel: data.customLabel || "",
          groupNameVisibility: data.groupNameVisibility === "hidden" ? "hidden" : "visible",
          iconVisibility: data.iconVisibility === "hidden" ? "hidden" : "visible",
          customLabelVisibility: data.customLabelVisibility === "hidden" ? "hidden" : "visible",
          handles: Array.isArray(data.handles) ? [...data.handles] : [],
          allowedNodeTypes: data.allowedNodeTypes || []
        });
      }, 0);
    } else {
       // Node ID is the same, check individual props if needed
       if (Array.isArray(data.handles) && JSON.stringify(data.handles) !== JSON.stringify(handles)) {
         setHandles([...data.handles]);
       }
       // Node ID is the same, only sync handles if necessary and different
       // Avoid syncing color here to prevent overwriting user selection with potentially stale data prop
       if (Array.isArray(data.handles) && JSON.stringify(data.handles) !== JSON.stringify(handles)) {
         setHandles([...data.handles]);
       }
    }
  }, [data]); // Keep dependency primarily on data; internal handle state compared manually

  // --- Handler for HandleManager changes ---
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);
  // --- End Handler ---

  // Update parent when state changes (triggered by user interaction)
  useEffect(() => {
    // Prevent updates during sync, if no node selected, or no update handler
    if (isSyncingRef.current || !prevNodeIdRef.current || !onUpdate) return;

    const updates: Partial<GroupNodeData> = {
      name,
      color,
      colorOpacity, // Include opacity in updates
      icon,
      customLabel,
      // hasIncomingHandle, // Removed
      // hasOutgoingHandle, // Removed
      groupNameVisibility,
      iconVisibility,
      customLabelVisibility,
      handles, // Include handles
      // type: 'group' as const, // Removed: Type is part of the node, not data
      allowedNodeTypes: data.allowedNodeTypes || []
    };

    // --- Change Detection Logic ---
    // We now compare the stringified 'updates' object directly with the last known state ('prevUpdateRef').
    // No need for the intermediate 'hasChanged' variable comparing internal state vs props.
    // --- End Change Detection Logic ---

    const updatesJson = JSON.stringify(updates); // Stringify includes handles now

    // Only call onUpdate if the current state (updatesJson) is different from the last known state (prevUpdateRef)
    if (updatesJson !== prevUpdateRef.current) {
      // console.log("[GroupConfigBox] Change detected, calling onUpdate", updates);
      prevUpdateRef.current = updatesJson; // Update ref *before* calling onUpdate
      onUpdate(updates);
    }
  }, [
    name,
    color,
    colorOpacity, // Add colorOpacity to dependency array
    icon,
    customLabel,
    // hasIncomingHandle, // Removed
    // hasOutgoingHandle, // Removed
    groupNameVisibility,
    iconVisibility,
    customLabelVisibility,
    handles, // Add handles to dependency array
    onUpdate, // Include onUpdate callback
    data.allowedNodeTypes // Include allowedNodeTypes to ensure it's included in updates

    // Change detection now relies on comparing updatesJson with prevUpdateRef.
  ]);

  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  // Handle color change
  const handleColorChange = (colorValue: string | undefined) => {
    // When color is set to undefined (Default), we need to ensure it stays undefined
    // and doesn't get replaced with a default gray color
    setColor(colorValue);
  };
  
  // Handle opacity change
  const handleOpacityChange = (opacityValue: number) => {
    setColorOpacity(opacityValue);
  };

  // Common input styling
  const labelClasses = "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1";

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        onCaptureImage={copySelectedAsImage} // Pass the image capture function
        showCaptureButton={true} // Show the capture button only for group nodes
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-5">
          Group Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Group name"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
          disabled={isDisabled}
        />
      </div>

      {/* Custom Label */}
      <div>
        <LabelWithVisibilityToggle
          label="Custom Label"
          visibility={customLabelVisibility}
          onToggleVisibility={() => toggleVisibility(customLabelVisibility, setCustomLabelVisibility)}
          className="mb-1 mt-3"
        />
        <AutoComplete
          value={customLabel}
          onChange={setCustomLabel}
          options={CUSTOM_LABEL_SUGGESTIONS}
          placeholder="Add custom label"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Icon"
          visibility={iconVisibility}
          onToggleVisibility={() => toggleVisibility(iconVisibility, setIconVisibility)}
          className="mb-1 mt-3"
        />
        <IconSelector
          value={icon}
          onChange={setIcon}
          portalRoot={portalRoot}
          disabled={isDisabled}
        />
      </div>

      {/* Color Picker */}
      <div>
        <label className={labelClasses}>Group Color</label>
        <ColorPicker
          value={color}
          onChange={handleColorChange}
          opacity={colorOpacity}
          onOpacityChange={handleOpacityChange}
          portalRoot={document.body}
          disabled={isDisabled}
          showOpacitySlider={color !== undefined && color !== "Default"}
        />
      </div>

      {/* --- Add Handle Manager --- */}
      <div className="pt-1">
        <HandleManager
          nodeId={nodeId}
          handles={handles}
          onChange={handleHandlesChange}
          maxHandles={6} // Or adjust as needed for groups
          portalRoot={portalRoot}
        />
      </div>
      {/* --- End Handle Manager --- */}
    </div>
  );
}
